import { type ExamRoomRequestPhysicalAttributes, type FeatureCheck } from '@exam-center/graphql';
import { Box, Divider, Flex, Text } from '@shared/components';

import { AROUND_AREA_FEATURES, PHYSICAL_FEATURES } from '../../containers';
import { ReadonlyPhysicalInfoFeatureCheckRow } from '../exam-room-info-details';
import ReadonlyDoorInfoRow from '../exam-room-info-details/readonly-door-info-row';
import { ReadonlyFormImagesInput } from '../readonly-form-images-input';

interface PhysicalInfoDetailsProps {
  resolvedInfo: ExamRoomRequestPhysicalAttributes | null | undefined;
}

export default function PhysicalInfoDetails(props: Readonly<PhysicalInfoDetailsProps>) {
  const { resolvedInfo } = props;

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(8),
        padding: theme.spacing(8),
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          gap: theme.spacing(6),
          [theme.breakpoints.down('desktop')]: {
            flexDirection: 'column',
          },
        })}
      >
        <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
          ลักษณะทางกายภาพ
        </Text>
        <Box
          sx={(theme) => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          {PHYSICAL_FEATURES.map((f) => {
            const feature = resolvedInfo?.[f.key as keyof ExamRoomRequestPhysicalAttributes] as
              | FeatureCheck
              | null
              | undefined;
            return (
              <ReadonlyPhysicalInfoFeatureCheckRow
                key={f.key}
                label={f.label}
                required={!!f.required}
                value={feature?.valid ?? null}
                additionalInfo={feature?.additionalInfo ?? ''}
                hasImages={!!f.hasImages}
                images={feature?.images ?? []}
                isReadonly={true}
                imageHelperText={f.imageInputLabel}
              />
            );
          })}
          <ReadonlyDoorInfoRow
            label='8.จำนวนประตูเปิดเข้า-ออกห้องสอบ'
            numberOfDoors={resolvedInfo?.entryExitDoors?.numberOfDoors ?? undefined}
            images={resolvedInfo?.entryExitDoors?.images ?? []}
          />
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              justifyContent: 'space-between',
              [theme.breakpoints.down('desktop')]: {
                flexDirection: 'column',
              },
            })}
          >
            <Box sx={{ minWidth: '320px' }}>
              <Text variant='body1' sx={{ fontWeight: 500 }}>
                9.หน้าห้องสอบ
              </Text>
            </Box>
            <ReadonlyFormImagesInput
              images={resolvedInfo?.frontOfExamRoom?.images ?? []}
              label='แนบรูปภาพบริเวณหน้าห้องสอบ'
            />
          </Flex>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              justifyContent: 'space-between',
              [theme.breakpoints.down('desktop')]: {
                flexDirection: 'column',
              },
            })}
          >
            <Box sx={{ minWidth: '320px' }}>
              <Text variant='body1' sx={{ fontWeight: 500 }}>
                10.ภายในห้องสอบเป็นภาพกว้าง
              </Text>
            </Box>
            <ReadonlyFormImagesInput
              images={resolvedInfo?.insideExamRoom?.images ?? []}
              label='แนบรูปภาพบริเวณภายในห้องสอบ'
            />
          </Flex>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          gap: theme.spacing(6),
          [theme.breakpoints.down('desktop')]: {
            flexDirection: 'column',
          },
        })}
      >
        <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
          ลักษณะบริเวณโดยรอบของห้องสอบ
        </Text>
        <Box
          sx={(theme) => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          {AROUND_AREA_FEATURES.map((f) => {
            const feature = resolvedInfo?.[f.key as keyof ExamRoomRequestPhysicalAttributes] as
              | FeatureCheck
              | null
              | undefined;
            return (
              <ReadonlyPhysicalInfoFeatureCheckRow
                key={f.key}
                label={f.label}
                required={!!f.required}
                value={feature?.valid ?? null}
                additionalInfo={feature?.additionalInfo ?? ''}
                hasImages={!!f.hasImages}
                images={feature?.images ?? []}
                isReadonly={true}
                imageHelperText={f.imageInputLabel}
              />
            );
          })}
        </Box>
      </Box>
    </Flex>
  );
}
